import { useEffect, useRef, useState } from 'react';
import { ESTILOS, useApariencia, type ModoColor } from '../lib/apariencia';
import { usePosicionDesplegable } from './ui-kit';

const MODOS: Array<{ id: ModoColor; nombre: string; icono: string }> = [
  { id: 'claro', nombre: 'Claro', icono: 'fa-solid fa-sun' },
  { id: 'oscuro', nombre: 'Oscuro', icono: 'fa-solid fa-moon' },
  { id: 'sistema', nombre: 'Sistema', icono: 'fa-solid fa-desktop' },
];

/** Botón de la cabecera para elegir modo claro/oscuro y estilo visual del panel. */
export default function AparienciaMenu() {
  const { apariencia, cambiarEstilo, cambiarModo } = useApariencia(true);
  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const iconoModo = MODOS.find(modo => modo.id === apariencia.modo)?.icono ?? 'fa-solid fa-sun';
  const botonRef = useRef<HTMLButtonElement>(null);
  const posicion = usePosicionDesplegable(abierto, botonRef);

  useEffect(() => {
    if (!abierto) return undefined;
    const alClic = (event: MouseEvent) => {
      if (!contenedorRef.current?.contains(event.target as Node)) setAbierto(false);
    };
    const alTecla = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', alClic);
    window.addEventListener('keydown', alTecla);
    return () => {
      document.removeEventListener('mousedown', alClic);
      window.removeEventListener('keydown', alTecla);
    };
  }, [abierto]);

  return (
    <div ref={contenedorRef} className="relative">
      <button
        ref={botonRef}
        type="button"
        aria-label="Apariencia"
        aria-expanded={abierto}
        title="Apariencia: modo oscuro y estilo"
        onClick={() => setAbierto(valor => !valor)}
        className="relative flex h-9 w-9 items-center justify-center rounded-[var(--radius-control)] border border-[var(--freisa-border)] bg-white text-sm text-[var(--freisa-black)] transition hover:-translate-y-0.5 hover:border-[var(--freisa-red)] hover:text-[var(--freisa-red)]"
      >
        <i className={iconoModo} aria-hidden="true" />
      </button>

      {abierto && (
        <div style={posicion} className="anim-pop-in absolute right-0 top-[calc(100%+12px)] z-30 max-h-[75vh] overflow-y-auto w-[min(330px,calc(100vw-2rem))] rounded-[var(--radius-panel)] border border-[var(--freisa-border)] bg-white p-4 shadow-[0_24px_60px_rgba(26,26,26,0.16)]">
          <p className="text-sm font-semibold text-[var(--freisa-text)]">Apariencia</p>
          <p className="mt-1 text-xs text-[var(--freisa-text-soft)]">Se guarda en este navegador.</p>

          <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-500">Modo</p>
          <div className="mt-2 grid grid-cols-3 gap-1 rounded-[var(--radius-control)] bg-stone-100 p-1">
            {MODOS.map(modo => {
              const activo = apariencia.modo === modo.id;
              return (
                <button
                  key={modo.id}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => cambiarModo(modo.id)}
                  className={`flex items-center justify-center gap-1.5 rounded-[calc(var(--radius-control)-4px)] px-2 py-2 text-xs font-semibold transition duration-200 ${
                    activo ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  <i className={modo.icono} aria-hidden="true" />
                  {modo.nombre}
                </button>
              );
            })}
          </div>

          <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-stone-500">Estilo</p>
          <div className="mt-2 space-y-1">
            {ESTILOS.map(estilo => {
              const activo = apariencia.estilo === estilo.id;
              return (
                <button
                  key={estilo.id}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => cambiarEstilo(estilo.id)}
                  className={`flex w-full items-center gap-3 rounded-[var(--radius-control)] border px-3 py-2 text-left transition duration-200 ${
                    activo ? 'border-[var(--freisa-red)] bg-stone-50' : 'border-transparent hover:bg-stone-50'
                  }`}
                >
                  <span className="flex shrink-0 -space-x-1.5">
                    {estilo.muestras.map(color => (
                      <span key={color} className="h-5 w-5 rounded-full border-2 border-white shadow-sm ring-1 ring-black/5" style={{ background: color }} />
                    ))}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-stone-900">{estilo.nombre}</span>
                    <span className="block truncate text-xs text-stone-500">{estilo.detalle}</span>
                  </span>
                  {activo && <i className="fa-solid fa-check ml-auto text-sm text-[var(--freisa-red)]" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
